import * as PopoverPrimitive from '@radix-ui/react-popover';
import clsx from 'clsx';
import { forwardRef } from 'react';
import Icon from '../Icon';
import { DEFAULT_TOOL_TIP_DELAY } from '../Tooltip/Tooltip.constants';
import { getSvgIconPath } from '../utils/svg';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { ToolbarButtonProps } from './ToolbarButton.types';

const popoverBorderClassNames = `
  tw:rounded-md
  tw:border
  tw:border-solid
  tw:border-sq-disabled-gray
  tw:dark:border-gray-500
`;

const triggerBackgroundClassNames = `
  tw:bg-transparent
  tw:hover:bg-sq-worksheetspanel-gray
  tw:active:bg-sq-worksheetspanel-gray
  tw:dark:bg-transparent
  tw:dark:hover:bg-sq-field-disabled-gray/30
  tw:dark:active:bg-sq-field-disabled-gray/30
`;

const activeBackgroundClassNames = `
  tw:bg-sq-overlay
  tw:active:bg-sq-overlay
  tw:dark:bg-sq-dark-disabled-gray
  tw:dark:hover:border-sq-dark-disabled-gray/30
  tw:dark:active:bg-sq-dark-disabled-gray/30
`;

const backgroundClassNames = 'tw:bg-sq-white tw:dark:bg-sq-dark-background';

const disabledClassNames = 'tw:cursor-not-allowed tw:opacity-50';

const PopoverContent = forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Content ref={ref} align={align} sideOffset={sideOffset} {...props} asChild />
));
PopoverContent.displayName = 'PopoverContent';

export const ToolbarButton = ({
  isSmall = false,
  label,
  icon,
  secondIcon,
  forceSmallIcon = false,
  tooltipText,
  id,
  popoverContent,
  extraClassNames,
  testId,
  tooltipTestId,
  tooltipOptions,
  isActive = false,
  isHtmlTooltip = false,
  hasArrow = false,
  disabled = false,
  isPrimaryAnSvg = false,
  primaryIconExtraClassNames,
  iconHeight = 24,
  iconWidth = 24,
  primaryIconViewbox = '0 0 24 24',
  popoverAlign = 'start',
  onClick,
  onHide,
}: ToolbarButtonProps) => {
  let tooltipData: QTipDataAttributes | undefined;

  if (tooltipText) {
    tooltipData = {
      'data-qtip-text': tooltipText,
      'data-qtip-placement': tooltipOptions?.position,
      'data-qtip-is-html': isHtmlTooltip,
      'data-qtip-testid': tooltipTestId,
      'data-qtip-delay': tooltipOptions?.delay ?? DEFAULT_TOOL_TIP_DELAY,
    };
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onHide && onHide();
    }
  };

  return (
    <PopoverPrimitive.Root defaultOpen={false} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger
        id={id}
        className={clsx(`tw:mx-sq-1.5 tw:border-none tw:bg-transparent tw:first:ml-0 tw:last:mr-0`, {
          active: isActive,
        })}
        disabled={disabled}
        data-testid={testId}
        onClick={(e) => {
          onClick && onClick(e);
        }}>
        <div
          {...tooltipData}
          className={clsx(
            'tw:flex tw:flex-col tw:items-center tw:rounded-md',
            {
              // eslint-disable-next-line better-tailwindcss/no-unknown-classes
              'active': isActive,
              [activeBackgroundClassNames]: isActive,
              [triggerBackgroundClassNames]: !isActive,
              'tw:px-1 tw:py-px': isSmall,
              'tw:px-2 tw:py-sq-5': !isSmall,
              [disabledClassNames]: disabled,
            },
            extraClassNames,
          )}>
          <span className="tw:whitespace-nowrap">
            {isPrimaryAnSvg ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`tw:text-sq-text-color tw:dark:text-sq-dark-text ${primaryIconExtraClassNames || ''}`}
                viewBox={primaryIconViewbox}
                height={`${iconHeight}px`}
                width={`${iconWidth}px`}
                fill="currentColor"
                data-testid={testId}>
                <path d={getSvgIconPath(icon)} />
              </svg>
            ) : (
              <Icon
                icon={icon}
                testId="firstIcon"
                type="text"
                size={!isSmall && !label && !secondIcon && !forceSmallIcon ? 'lg' : 'sm'}
                extraClassNames="tw:text-sq-text-color tw:dark:text-sq-dark-text"
              />
            )}

            {secondIcon && <Icon icon={secondIcon} type="text" testId="secondIcon" />}
            {popoverContent ? (
              <Icon
                icon="fc-arrow-dropdown"
                extraClassNames="tw:text-sq-text-color tw:dark:text-sq-dark-text tw:ml-sq-3 tw:text-2"
                type="text"
                size="sm"
              />
            ) : undefined}
          </span>
          {!isSmall && <small className="tw:text-sq-10 tw:text-sq-text-color tw:dark:text-sq-dark-text">{label}</small>}
        </div>
      </PopoverPrimitive.Trigger>
      {popoverContent ? (
        <PopoverContent sideOffset={2} align={popoverAlign}>
          <div
            className={clsx(
              'tw:relative tw:z-1000 tw:min-w-6 tw:p-2 tw:outline-none tw:focus-visible:outline-none',
              backgroundClassNames,
              popoverBorderClassNames,
            )}>
            {hasArrow && (
              <PopoverPrimitive.Arrow asChild>
                <div className="tw:-mt-sq-7 tw:h-sq-15 tw:w-sq-15 tw:rotate-45 tw:border-r tw:border-b tw:border-sq-disabled-gray tw:bg-sq-white tw:fill-transparent tw:dark:border-gray-500 tw:dark:bg-sq-dark-background" />
              </PopoverPrimitive.Arrow>
            )}
            {popoverContent}
          </div>
        </PopoverContent>
      ) : undefined}
    </PopoverPrimitive.Root>
  );
};
